import { GridDataEntry } from 'controls/grid/gridDataEntry';
import { GridPlugin } from 'controls/grid/gridConfiguration';
import { GridStore } from 'controls/grid/gridStore';
import { addOrUpdateRule, RuleDefinition, RulesConfiguration } from 'controls/queryBuilder/ruleDefinition';
import { makeAutoObservable, toJS } from 'mobx';
import { deserialize } from 'serializr';

type StateOverridePluginConfig<T extends object, DataItem extends GridDataEntry> = {
	customPayloadDefaults?: Record<string, any>;
	dataObject?: T;
	enabled?: boolean;
	fields?: ([keyof T, string] | keyof T)[];
	filters?: RuleDefinition;
	getFilters?: (filters: RuleDefinition) => void;
	getSorting?: () => { [TField in keyof DataItem]?: 'asc' | 'desc' };
};

export class StateOverridePlugin<T extends object, DataItem extends GridDataEntry> implements GridPlugin<DataItem> {
	gridStore: GridStore<DataItem>;
	config: StateOverridePluginConfig<T, DataItem>;

	constructor(config: StateOverridePluginConfig<T, DataItem>) {
		this.config = config;
		makeAutoObservable(this);
	}

	async attach(store: GridStore<DataItem>) {
		this.gridStore = store;
	}

	afterStoreInitialized() {
		this.overrideFilters();
	}

	overrideFilters = () => {
		const incomingFilterObjectIsEmpty = !this.config.filters?.order?.length;
		const incomingFilterObjectExists = this.config.filters;

		if (this.config.enabled === false || (!this.config.dataObject && !incomingFilterObjectExists)) return;

		const filters: RuleDefinition = incomingFilterObjectIsEmpty
			? RuleDefinition.emptyGroup()
			: deserialize(RuleDefinition, toJS(this.config.filters));

		if (this.config.dataObject) {
			const availableFilters = Object.keys(this.gridStore.filtersConfiguration);
			for (const fieldsEntry of this.config.fields ?? availableFilters) {
				let propertyName: keyof T | string = null;
				let gridFieldName: string = null;

				if (Array.isArray(fieldsEntry)) {
					propertyName = fieldsEntry[0];
					gridFieldName = fieldsEntry[1];
				} else {
					propertyName = fieldsEntry;
					gridFieldName = fieldsEntry as string;
				}

				const value = this.config.dataObject[propertyName as keyof T];
				if (!value || !availableFilters.includes(propertyName.toString())) continue;

				const rule = RuleDefinition.emptyRule(gridFieldName, 'like');

				if (Array.isArray(value)) {
					if (value.length == 0) continue;

					rule.properties.value = value;
					rule.properties.operator = 'multiselect_equals';
				} else {
					rule.properties.value[0] = value?.toString();
				}

				addOrUpdateRule(filters, rule);
			}
		}

		this.config.getFilters?.(filters);

		//the condition incomingFilterObjectExists && incomingFilterObjectIsEmpty might looks weird
		//but that actually mean that we got on a page with predefined filter but that filter mean 'everything' so we should
		//clean current filter anyway to show all records

		if (
			filters.order.length > 0 ||
			(incomingFilterObjectExists && incomingFilterObjectIsEmpty) ||
			this.config.enabled === true
		) {
			this.gridStore.state.switchToTempView();
			this.gridStore.state.filters = filters;
			this.gridStore.state.searchString = '';
			this.gridStore.state.collapsedGroups.length = 0;

			const customPayload = this.config.customPayloadDefaults ?? this.gridStore.config.defaults?.payload;

			if (customPayload) {
				this.gridStore.state.customPayload = JSON.parse(JSON.stringify(customPayload)) as Record<string, unknown>;
			}
		}

		if (this.config.getSorting) {
			const sorting = this.config.getSorting();
			if (sorting) {
				this.gridStore.state.clearSorting();
				for (const [field, direction] of Object.entries<'asc' | 'desc'>(sorting)) {
					const column = this.gridStore.state.columns.find((x) => x.field == field);
					if (!column) {
						continue;
					}

					this.gridStore.state.sortingOrder.push(field);
					column.sorting = direction;
				}
			}
		}
	};

	destroy() {}
}
